import { DataTableNew } from '@/components/shared/DataTable';
import StatusBadge from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/Button';
import { TenantService } from '@/services/tenantService';
import { formatDate } from '@/utils/dateUtils';
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import useLiveResource from '@/hooks/useLiveResource';

const RecentTenants = () => {

    // Router navigation hook for "View All" action
    const navigate = useNavigate();

    const [silentFetch, setSilentFetch] = useState(false);

    useEffect(() => {
        if (!silentFetch) return;
        const timer = setTimeout(() => setSilentFetch(false), 200);
        return () => clearTimeout(timer);
    }, [silentFetch]);

    // A tenant created, edited or (de)activated elsewhere otherwise only
    // shows up here on the next manual page load.
    useLiveResource({
        keys: "tenant",
        onChange: () => setSilentFetch(true),
    });

    // Table column definitions (memoized to avoid re-creation on rerenders)
    const COLUMNS = useMemo(() => {
        return [

            // Tenant name column
            {
                key: "name",
                label: "Tenant Name",
                align: 'left',
                sortable: false,
                render: (val) => (
                    <span className="text-left font-medium text-[13px]">
                        {val ?? "—"}
                    </span>
                ),
            },

            // Tenant slug identifier column
            {
                key: "slug",
                label: "Slug",
                align: 'left',
                sortable: false,
                render: (val) => (
                    <span className="text-left font-medium text-[13px]">
                        {val ?? "—"}
                    </span>
                ),
            },

            // Maximum allowed users per tenant
            {
                key: "maxUsers",
                label: "Max Users",
                align: 'center',
                sortable: false,
                render: (val) => (
                    <span className="text-left font-medium text-[13px]">
                        {val ?? "—"}
                    </span>
                ),
            },

            // Currently active users in tenant
            {
                key: "activeUsers",
                label: "Total Active Users",
                align: 'center',
                sortable: false,
                render: (val) => (
                    <span className="text-left font-medium text-[13px]">
                        {val ?? "—"}
                    </span>
                ),
            },

            // Tenant status (active/inactive)
            {
                key: "isActive",
                label: "Status",
                align: 'center',
                sortable: false,
                render: (val) => (
                    val === null ? "—" : <StatusBadge isActive={val} />
                ),
            },

            // Tenant creation date
            {
                key: "createdAt",
                label: "Created",
                align: 'center',
                sortable: false,
                render: (val) => (
                    <span className="text-text-primary text-[12px]">
                        {val ? formatDate(val) : "—"}
                    </span>
                ),
            },
        ];
    }, []);

    // Navigate to full tenant management page
    const handleViewAllTenants = () => {
        navigate("/super-admin/tenant");
    };

    return (
        <div className="rounded-xl border border-border-primary bg-background-secondary">

            {/* Header section with title + action button */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border-primary">
                <p className="text-[13px] font-semibold text-text-primary m-0">
                    Recent Tenants
                </p>

                <Button
                    onClick={handleViewAllTenants}
                    className="h-7 px-3 flex items-center gap-1.5 text-[12px] font-medium rounded-md border border-border-primary bg-background-secondary text-text-secondary hover:text-text-primary hover:bg-background-primary/30 hover:border-border-primary transition-all duration-200"
                >
                    View All
                </Button>
            </div>

            {/* Data table showing recent tenants */}
            <DataTableNew
                key="recent-tenants"
                className="rounded-none"
                columns={COLUMNS}
                showPagination={true}
                showPaginationLabel={false}
                sortable={false}

                // API source for tenant list
                api={TenantService.fetchTenantList}
                silentFetch={silentFetch}

                // Response key path for table data
                dataPath="tenants"

                // Default sorting configuration
                defaultSort={{
                    key: "created_at",
                    order: "desc",
                }}

                // Limit number of rows shown
                itemSize={5}

                // Row Click to redirect the user to that tenant
                onRowClick={row => navigate(`/super-admin/tenant-details/${row.id}`)}
            />
        </div>
    )
}

export default RecentTenants